import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Copy, Mail, MessageCircle, Link2, CheckCircle2, Clock, XCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid: { label: 'Paid', icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expired: { label: 'Expired', icon: Clock, color: 'bg-slate-100 text-slate-600 border-slate-200' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export default function PaymentLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showShare, setShowShare] = useState(null);
  const [form, setForm] = useState({
    amount: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '+91',
    description: '',
    gateway: 'cashfree',
    expiresInHours: 72,
  });
  const [creating, setCreating] = useState(false);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/payments/links`, {
        params: filter !== 'all' ? { status: filter } : {},
        withCredentials: true,
      });
      setLinks(res.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load links');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const handleCreate = async () => {
    if (!form.amount || !form.customerName || !form.customerEmail || !form.customerPhone) {
      toast.error('Fill required fields');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/payments/links`, {
        ...form,
        amount: parseFloat(form.amount),
        expiresInHours: parseInt(form.expiresInHours, 10) || 72,
      }, { withCredentials: true });
      toast.success('Payment link created');
      setShowCreate(false);
      setShowShare(res.data);
      setForm({ amount: '', customerName: '', customerEmail: '', customerPhone: '+91', description: '', gateway: 'cashfree', expiresInHours: 72 });
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  const sendEmail = async (linkId) => {
    try {
      const res = await axios.post(`${API}/api/payments/links/${linkId}/send`, { via: 'email' }, { withCredentials: true });
      toast.success(res.data.message || 'Email sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    }
  };

  const cancelLink = async (linkId) => {
    if (!window.confirm('Cancel this payment link? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/payments/links/${linkId}`, { withCredentials: true });
      toast.success('Link cancelled');
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <div className="space-y-6" data-testid="payment-links-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payment Links</h1>
          <p className="text-sm text-slate-500 mt-1">Create and share branded payment links — via Cashfree, Razorpay, or Stripe.</p>
        </div>
        <Button
          data-testid="new-payment-link-btn"
          onClick={() => setShowCreate(true)}
          className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl shadow-md"
        >
          <Plus className="w-4 h-4 mr-2" /> New Payment Link
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'pending', 'paid', 'expired', 'cancelled'].map((s) => (
          <button
            key={s}
            data-testid={`filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 text-xs font-semibold rounded-full border transition ${
              filter === s ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Links list */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : links.length === 0 ? (
          <div className="py-20 text-center text-slate-500 text-sm">
            <Link2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            No payment links yet. Click "New Payment Link" to create one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Link ID</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Gateway</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => {
                const meta = STATUS_META[l.status] || STATUS_META.pending;
                const Icon = meta.icon;
                const shareUrl = l.short_url || `${window.location.origin}/pay/${l.payment_ref || l.token}`;
                return (
                  <tr key={l.linkId} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`link-row-${l.linkId}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{l.linkId}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{l.customerName}</div>
                      <div className="text-xs text-slate-500">{l.customerEmail}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold">INR {Number(l.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs bg-slate-100 rounded-full capitalize">{l.gateway}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${meta.color} border font-medium flex items-center gap-1 w-fit`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {new Date(l.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          data-testid={`copy-link-${l.linkId}`}
                          variant="ghost" size="sm"
                          onClick={() => copyLink(shareUrl)}
                          title="Copy link"
                        ><Copy className="w-4 h-4" /></Button>
                        {l.status === 'pending' && (
                          <>
                            <Button
                              data-testid={`email-link-${l.linkId}`}
                              variant="ghost" size="sm"
                              onClick={() => sendEmail(l.linkId)}
                              title="Email customer"
                            ><Mail className="w-4 h-4" /></Button>
                            <a
                              href={`https://wa.me/${(l.customerPhone || '').replace(/\D/g, '')}?text=${encodeURIComponent(`Please complete your payment: ${shareUrl}`)}`}
                              target="_blank" rel="noreferrer"
                              title="WhatsApp"
                              data-testid={`whatsapp-link-${l.linkId}`}
                            >
                              <Button variant="ghost" size="sm"><MessageCircle className="w-4 h-4 text-green-600" /></Button>
                            </a>
                            <Button
                              data-testid={`cancel-link-${l.linkId}`}
                              variant="ghost" size="sm"
                              onClick={() => cancelLink(l.linkId)}
                              title="Cancel link"
                            ><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg" data-testid="create-link-dialog">
          <DialogHeader>
            <DialogTitle>Create Payment Link</DialogTitle>
            <DialogDescription>Send a branded, secure payment link to your customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Amount (INR) *</Label>
              <Input
                data-testid="create-amount-input"
                type="number" min="1"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="10000"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  data-testid="create-name-input"
                  value={form.customerName}
                  onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                  placeholder="Rahul Sharma"
                />
              </div>
              <div>
                <Label>Gateway</Label>
                <Select value={form.gateway} onValueChange={(v) => setForm({ ...form, gateway: v })}>
                  <SelectTrigger data-testid="create-gateway-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cashfree">Cashfree</SelectItem>
                    <SelectItem value="razorpay">Razorpay</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email *</Label>
                <Input
                  data-testid="create-email-input"
                  type="email"
                  value={form.customerEmail}
                  onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
                  placeholder="rahul@example.com"
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  data-testid="create-phone-input"
                  value={form.customerPhone}
                  onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                  placeholder="+91 9876543210"
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                data-testid="create-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Bali 5N/6D Package - Advance Payment"
              />
            </div>
            <div>
              <Label>Expires In (hours)</Label>
              <Input
                type="number" min="1" max="720"
                value={form.expiresInHours}
                onChange={(e) => setForm({ ...form, expiresInHours: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              data-testid="create-link-submit-btn"
              onClick={handleCreate} disabled={creating}
              className="bg-teal-700 hover:bg-teal-800 text-white"
            >
              {creating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating</> : 'Create & Get Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={!!showShare} onOpenChange={(o) => !o && setShowShare(null)}>
        <DialogContent data-testid="share-link-dialog">
          <DialogHeader>
            <DialogTitle>Payment Link Created</DialogTitle>
            <DialogDescription>Share this secure link with your customer.</DialogDescription>
          </DialogHeader>
          {showShare && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="text-xs text-slate-500 mb-1">Shareable URL</div>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={showShare.short_url || `${window.location.origin}/pay/${showShare.payment_ref || showShare.token}`}
                    className="flex-1 bg-white px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono"
                    data-testid="share-url-input"
                  />
                  <Button
                    data-testid="share-copy-btn"
                    size="sm"
                    onClick={() => copyLink(showShare.short_url || `${window.location.origin}/pay/${showShare.payment_ref || showShare.token}`)}
                  ><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => sendEmail(showShare.linkId)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="share-email-btn"
                ><Mail className="w-4 h-4 mr-2" /> Email</Button>
                <a href={showShare.whatsappShare} target="_blank" rel="noreferrer" className="flex-1">
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="share-whatsapp-btn">
                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
